"use client";

import * as React from "react";

import { Dialog } from "~/components/ui/dialog";
import { fallBackToOriginal, thumbnailImage } from "~/lib/image-loader";
import { reviewMediaRejection } from "~/lib/review-media";
import type { ReviewsMediaConfig } from "~/server/api/schemas/store-settings";
import { api } from "~/trpc/react";
import { useTranslator } from "~/i18n/provider";

/**
 * P5-17 · Review-media client pieces: the form's upload engine (validate →
 * presigned PUT with progress; attach happens at review submit, once the
 * review row exists) and the card's approved-media thumbnails + lightbox.
 */

export interface ReviewMediaView {
  id: string;
  type: "IMAGE" | "VIDEO";
  mimeType: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  url: string;
  width: number | null;
  height: number | null;
}

export type ReviewUploadStatus = "uploading" | "done" | "rejected" | "failed";

export interface ReviewUploadItem {
  clientId: string;
  status: ReviewUploadStatus;
  progress: number;
  reason?: string;
  /** Local preview (object URL); shown for every state. */
  previewUrl: string;
  kind: "IMAGE" | "VIDEO";
  /** Set once the PUT completes — what `attach` consumes at submit time. */
  storageKey?: string;
  width?: number;
  height?: number;
}

function xhrPut(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable)
        onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Storage responded ${xhr.status}.`));
    xhr.onerror = () => reject(new Error("Network error mid-transfer."));
    xhr.send(file);
  });
}

async function imageDims(
  file: File,
): Promise<{ width?: number; height?: number }> {
  try {
    const bmp = await createImageBitmap(file);
    const dims = { width: bmp.width, height: bmp.height };
    bmp.close();
    return dims;
  } catch {
    return {};
  }
}

let nextClientId = 0;

/**
 * Upload engine for the review form. Files PUT straight to storage; `items`
 * with status "done" carry the storageKey the caller attaches after the
 * review submit succeeds. `slotsLeft` counts existing attachments so the
 * per-review cap holds across edits.
 */
export function useReviewMediaUpload(
  config: ReviewsMediaConfig,
  existingCount: number,
  /**
   * P15-11 · A review-invite token, for a guest who has no session for the
   * presign to hang off. Passed straight through to the router, which mints
   * the upload key under an order-scoped prefix instead of a user-scoped one.
   */
  orderToken?: string,
) {
  const createUploadUrl = api.reviewMedia.createUploadUrl.useMutation();
  const [items, setItems] = React.useState<ReviewUploadItem[]>([]);

  const patch = React.useCallback(
    (clientId: string, changes: Partial<ReviewUploadItem>) => {
      setItems((prev) =>
        prev.map((i) => (i.clientId === clientId ? { ...i, ...changes } : i)),
      );
    },
    [],
  );

  const slotsLeft =
    config.maxPerReview -
    existingCount -
    items.filter((i) => i.status === "done" || i.status === "uploading").length;

  const addFiles = React.useCallback(
    (files: Iterable<File>) => {
      let slots = slotsLeft;
      for (const file of files) {
        const clientId = `rm${nextClientId++}`;
        const kind = file.type.startsWith("video/")
          ? ("VIDEO" as const)
          : ("IMAGE" as const);
        let reason = reviewMediaRejection(file, config);
        if (!reason && slots <= 0) {
          reason = `At most ${config.maxPerReview} attachments per review.`;
        }
        const item: ReviewUploadItem = {
          clientId,
          status: reason ? "rejected" : "uploading",
          progress: 0,
          reason: reason ?? undefined,
          previewUrl: URL.createObjectURL(file),
          kind,
        };
        setItems((prev) => [...prev, item]);
        if (reason) continue;
        slots -= 1;
        void (async () => {
          try {
            const { uploadUrl, storageKey } = await createUploadUrl.mutateAsync(
              {
                mimeType: file.type,
                sizeBytes: file.size,
                ...(orderToken ? { orderToken } : {}),
              },
            );
            await xhrPut(uploadUrl, file, (pct) =>
              patch(clientId, { progress: pct }),
            );
            const dims = kind === "IMAGE" ? await imageDims(file) : {};
            patch(clientId, {
              status: "done",
              progress: 100,
              storageKey,
              ...dims,
            });
          } catch (err) {
            patch(clientId, {
              status: "failed",
              reason:
                err instanceof Error
                  ? err.message
                  : "Upload failed — try again.",
            });
          }
        })();
      }
    },
    [config, createUploadUrl, orderToken, patch, slotsLeft],
  );

  const remove = React.useCallback((clientId: string) => {
    setItems((prev) => {
      const item = prev.find((i) => i.clientId === clientId);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.clientId !== clientId);
    });
  }, []);

  const reset = React.useCallback(() => {
    setItems((prev) => {
      for (const i of prev) URL.revokeObjectURL(i.previewUrl);
      return [];
    });
  }, []);

  return {
    items,
    addFiles,
    remove,
    reset,
    slotsLeft,
    uploading: items.some((i) => i.status === "uploading"),
    /** Uploads ready to attach once the review exists. */
    ready: items.filter(
      (i): i is ReviewUploadItem & { storageKey: string } =>
        i.status === "done" && !!i.storageKey,
    ),
  };
}

const thumbCls =
  "relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-surface-2";

function PlayGlyph() {
  return (
    <span
      aria-hidden
      className="absolute inset-0 flex items-center justify-center bg-black/30 text-lg text-white"
    >
      ▶
    </span>
  );
}

/**
 * Approved-media thumbnails on a review card; click opens the lightbox
 * (focus-trapped, Esc-dismissable via DS Dialog) with prev/next.
 */
export function ReviewMediaThumbs({
  media,
  author,
}: {
  media: ReviewMediaView[];
  author: string;
}) {
  const [openAt, setOpenAt] = React.useState<number | null>(null);
  if (media.length === 0) return null;
  return (
    <>
      <ul
        className="mt-2.5 flex flex-wrap gap-2"
        aria-label={`Photos and videos from ${author}`}
      >
        {media.map((m, i) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => setOpenAt(i)}
              className={`${thumbCls} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]`}
              aria-label={`Open ${m.type === "VIDEO" ? "video" : "photo"} ${i + 1} of ${media.length}`}
            >
              {m.type === "VIDEO" ? (
                <>
                  <video
                    src={m.url}
                    muted
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                  <PlayGlyph />
                </>
              ) : (
                // eslint-disable-next-line @next/next/no-img-element -- fixed 64 px box; resized via the loader, original on refusal
                <img
                  {...thumbnailImage(m.url, 64)}
                  onError={fallBackToOriginal(m.url)}
                  alt={`Review photo ${i + 1} from ${author}`}
                  className="h-full w-full object-cover"
                />
              )}
            </button>
          </li>
        ))}
      </ul>
      <ReviewMediaLightbox
        media={media}
        author={author}
        index={openAt}
        onClose={() => setOpenAt(null)}
        onNavigate={setOpenAt}
      />
    </>
  );
}

function ReviewMediaLightbox({
  media,
  author,
  index,
  onClose,
  onNavigate,
}: {
  media: ReviewMediaView[];
  author: string;
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const t = useTranslator();
  const item = index !== null ? media[index] : undefined;
  const navBtn =
    "rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-fg hover:bg-surface-2 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]";
  return (
    <Dialog
      open={item !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      ariaLabel={`Review media from ${author}`}
      className="max-w-3xl"
    >
      {item ? (
        <figure>
          <div className="flex max-h-[70vh] items-center justify-center overflow-hidden rounded-md bg-surface-2">
            {item.type === "VIDEO" ? (
              <video
                src={item.url}
                controls
                autoPlay
                className="max-h-[70vh] w-auto max-w-full"
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element -- lightbox is max-w-3xl (768 px); original on refusal
              <img
                key={item.id}
                {...thumbnailImage(item.url, 768)}
                onError={fallBackToOriginal(item.url)}
                alt={`Review photo ${(index ?? 0) + 1} from ${author}`}
                className="max-h-[70vh] w-auto max-w-full object-contain"
              />
            )}
          </div>
          <figcaption className="mt-3 flex items-center justify-between gap-2 text-xs text-fg-muted">
            <span>
              {(index ?? 0) + 1} of {media.length} · {author}
            </span>
            <span className="flex gap-2">
              <button
                type="button"
                className={navBtn}
                disabled={index === 0}
                onClick={() => onNavigate((index ?? 0) - 1)}
              >
                ← Prev
              </button>
              <button
                type="button"
                className={navBtn}
                disabled={index === media.length - 1}
                onClick={() => onNavigate((index ?? 0) + 1)}
              >
                {t("browse.next")}
              </button>
              <button type="button" className={navBtn} onClick={onClose}>
                Close
              </button>
            </span>
          </figcaption>
        </figure>
      ) : null}
    </Dialog>
  );
}

/**
 * The form's attachment strip: existing media (with moderation state) +
 * in-flight uploads + the add button. Pure presentation — state lives in
 * `useReviewMediaUpload` / the parent form.
 */
export function ReviewMediaUploader({
  config,
  existing,
  onRemoveExisting,
  upload,
}: {
  config: ReviewsMediaConfig;
  existing: ReviewMediaView[];
  /**
   * P15-11 · Optional: the invite form has no existing attachments to remove
   * (a guest cannot edit a posted review), so it passes none and needs no
   * handler for one.
   */
  onRemoveExisting?: (mediaId: string) => void;
  upload: ReturnType<typeof useReviewMediaUpload>;
}) {
  const t = useTranslator();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const accept = config.allowVideo
    ? "image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
    : "image/jpeg,image/png,image/webp";
  const removeCls =
    "absolute right-0.5 top-0.5 rounded bg-black/55 px-1 text-[10px] font-bold leading-4 text-white hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]";

  return (
    <div className="mb-3">
      <span className="mb-1 block text-xs font-medium text-fg-muted">
        Photos {config.allowVideo ? "& videos " : ""}
        <span className="font-normal">
          · optional, up to {config.maxPerReview}
        </span>
      </span>
      <div className="flex flex-wrap items-start gap-2">
        {existing.map((m) => (
          <span key={m.id} className={thumbCls}>
            {m.type === "VIDEO" ? (
              <>
                <video
                  src={m.url}
                  muted
                  preload="metadata"
                  className="h-full w-full object-cover"
                />
                <PlayGlyph />
              </>
            ) : (
              // eslint-disable-next-line @next/next/no-img-element -- customer upload thumb
              <img
                src={m.url}
                alt={t("reviews.attachedMedia")}
                className="h-full w-full object-cover"
              />
            )}
            {m.status !== "APPROVED" ? (
              <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 text-center text-[9px] font-semibold uppercase text-white">
                {m.status === "PENDING" ? "In review" : "Rejected"}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => onRemoveExisting?.(m.id)}
              className={removeCls}
              aria-label={t("reviews.removeAttachment")}
            >
              ✕
            </button>
          </span>
        ))}
        {upload.items.map((i) => (
          <span key={i.clientId} className={thumbCls}>
            {i.kind === "VIDEO" ? (
              <>
                <video
                  src={i.previewUrl}
                  muted
                  className="h-full w-full object-cover"
                />
                <PlayGlyph />
              </>
            ) : (
              // eslint-disable-next-line @next/next/no-img-element -- local object URL preview
              <img
                src={i.previewUrl}
                alt={t("reviews.uploadPreview")}
                className="h-full w-full object-cover"
              />
            )}
            {i.status === "uploading" ? (
              <span
                role="progressbar"
                aria-valuenow={i.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Uploading"
                className="absolute inset-x-0 bottom-0 h-1 bg-black/30"
              >
                <span
                  className="block h-full bg-primary"
                  style={{ width: `${i.progress}%` }}
                />
              </span>
            ) : null}
            {i.status === "rejected" || i.status === "failed" ? (
              <span className="absolute inset-0 flex items-center justify-center bg-danger/70 text-[9px] font-semibold text-white">
                {i.status === "rejected" ? "Rejected" : "Failed"}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => upload.remove(i.clientId)}
              className={removeCls}
              aria-label={t("reviews.removeUpload")}
            >
              ✕
            </button>
          </span>
        ))}
        {upload.slotsLeft > 0 ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-border text-2xl leading-none text-fg-muted hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            aria-label={t("reviews.addMedia")}
          >
            +
          </button>
        ) : null}
      </div>
      {upload.items
        .filter((i) => i.reason)
        .map((i) => (
          <p key={i.clientId} role="alert" className="mt-1 text-xs text-danger">
            {i.reason}
          </p>
        ))}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) upload.addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
